import dayjs, { Dayjs } from 'dayjs';
import { FasceOrarie } from './FasceOrarieUtils';

/**
 * Ore mostrate dalle viste a griglia oraria (Giorno, 3 Giorni, Lavorativo,
 * Settimana): ore intere, dall'inizio compreso alla fine esclusa, come
 * startHour ed endHour del calendario.
 */
export interface FinestraOraria {
  startHour: number;
  endHour: number;
}

/**
 * Le ore di sempre dell'agenda negozio (e dei calendari che partono dalle 5):
 * con Ore «Tutte» nel riquadro Visualizzazione e nei giorni senza orari. Un
 * calendario con ore sue (il giornaliero Utente parte dalle 7) passa la sua
 * `predefinita`.
 */
export const FINESTRA_PREDEFINITA: Readonly<FinestraOraria> = {
  startHour: 5,
  endHour: 24,
};

/**
 * Orari di un giorno: le fasce di una banda sola (agenda negozio) o di più
 * bande da unire (Personale con più punti vendita, colonne dei giornalieri).
 * Una fascia conta solo con inizio prima della fine.
 */
export type OrariGiorno = FasceOrarie | readonly FasceOrarie[];

/** La banda calendar dello sfondo: 'YYYY-MM-DD' → fasce del giorno. */
export type OrariFinestra = Readonly<
  Record<string, OrariGiorno | null | undefined>
>;

/** Appuntamento della lista dell'agenda: contano solo inizio e fine. */
export interface AppuntamentoFinestra {
  StartTime: string | Date;
  EndTime: string | Date;
}

/**
 * Appuntamento che finisce in un giorno diverso da quello in cui inizia.
 * - `escludi` (FOCUS CLOUD): il calendario lo mette nella riga «tutto il
 *   giorno», che è nascosta, anche quando finisce alle 00:00 del giorno dopo.
 *   Non si vede comunque, quindi non allarga la finestra.
 * - `finoAlle24` (console): resta nelle colonne orarie del giorno in cui
 *   inizia, quindi conta fino alle 24.
 */
export type RegolaMezzanotte = 'escludi' | 'finoAlle24';

const MINUTI_GIORNO = 24 * 60;

// Intervallo in minuti dalla mezzanotte: inizio compreso, fine esclusa
interface Intervallo {
  inizio: number;
  fine: number;
}

// 'HH:mm:ss' in minuti dalla mezzanotte; null se manca o non è un orario
const getMinutiOrario = (orario?: string | null): number | null => {
  if (!orario) return null;
  const [ore, minuti = '0', secondi = '0'] = orario.split(':');
  const valore = Number(ore) * 60 + Number(minuti) + Number(secondi) / 60;
  return Number.isFinite(valore) ? valore : null;
};

const getMinutiGiorno = (data: Dayjs): number =>
  data.hour() * 60 + data.minute() + data.second() / 60;

const unisci = (
  a: Intervallo | null,
  b: Intervallo | null
): Intervallo | null => {
  if (a === null) return b;
  if (b === null) return a;
  return {
    inizio: Math.min(a.inizio, b.inizio),
    fine: Math.max(a.fine, b.fine),
  };
};

// Una fascia vale solo con inizio prima della fine: le altre (00:00-00:00, una
// fine alle 00:00:00) non colorano nessuna cella, quindi non contano
const getFascia = (
  inizio?: string | null,
  fine?: string | null
): Intervallo | null => {
  const minutiInizio = getMinutiOrario(inizio);
  const minutiFine = getMinutiOrario(fine);
  if (minutiInizio === null || minutiFine === null) return null;
  return minutiInizio < minutiFine
    ? { inizio: minutiInizio, fine: minutiFine }
    : null;
};

const isElencoFasce = (
  orariGiorno: OrariGiorno
): orariGiorno is readonly FasceOrarie[] => Array.isArray(orariGiorno);

// Unione delle fasce dei giorni, cercate per data esatta come fa il colore
// delle celle, così la finestra coincide con la parte bianca della griglia. Con
// più bande nello stesso giorno contano tutte, ognuna valida per conto suo
const getIntervalloFasce = (
  giorni: readonly string[],
  orari: OrariFinestra | undefined
): Intervallo | null => {
  let intervallo: Intervallo | null = null;
  for (const giorno of giorni) {
    const orariGiorno = orari?.[giorno];
    if (!orariGiorno) continue;
    const elenco = isElencoFasce(orariGiorno) ? orariGiorno : [orariGiorno];
    for (const fasce of elenco) {
      intervallo = unisci(
        intervallo,
        getFascia(fasce.startTime1, fasce.endTime1)
      );
      intervallo = unisci(
        intervallo,
        getFascia(fasce.startTime2, fasce.endTime2)
      );
    }
  }
  return intervallo;
};

// Unione degli appuntamenti che iniziano in uno dei giorni, di qualunque stato,
// forzati e non validi compresi
const getIntervalloAppuntamenti = (
  giorni: readonly string[],
  appuntamenti: readonly AppuntamentoFinestra[],
  regolaMezzanotte: RegolaMezzanotte
): Intervallo | null => {
  const giorniFinestra = new Set(giorni);
  let intervallo: Intervallo | null = null;
  for (const appuntamento of appuntamenti) {
    const inizio = dayjs(appuntamento.StartTime);
    if (!inizio.isValid()) continue;
    if (!giorniFinestra.has(inizio.format('YYYY-MM-DD'))) continue;
    const minutiInizio = getMinutiGiorno(inizio);
    const fine = dayjs(appuntamento.EndTime);
    let minutiFine: number;
    if (!fine.isValid() || !fine.isAfter(inizio)) {
      // Dato sporco, fine mancante o non dopo l'inizio: conta solo l'inizio,
      // cioè la riga dell'ora in cui comincia
      minutiFine = minutiInizio + 1;
    } else if (!fine.isSame(inizio, 'day')) {
      if (regolaMezzanotte === 'escludi') continue;
      minutiFine = MINUTI_GIORNO;
    } else {
      minutiFine = getMinutiGiorno(fine);
    }
    intervallo = unisci(intervallo, { inizio: minutiInizio, fine: minutiFine });
  }
  return intervallo;
};

// Ore intere senza margine: inizio per difetto, fine per eccesso, dentro 0-24 e
// con almeno un'ora
const arrotonda = ({ inizio, fine }: Intervallo): FinestraOraria => {
  const startHour = Math.min(23, Math.max(0, Math.floor(inizio / 60)));
  const endHour = Math.min(24, Math.max(startHour + 1, Math.ceil(fine / 60)));
  return { startHour, endHour };
};

/**
 * Allarga una finestra agli appuntamenti che iniziano nei giorni indicati, senza
 * mai stringerla. Serve anche da sola, quando gli appuntamenti nuovi sono già
 * arrivati e gli orari no: la finestra mostrata può solo crescere, così nessun
 * appuntamento resta fuori.
 */
export function allargaFinestraOraria(
  finestra: Readonly<FinestraOraria>,
  giorni: readonly string[],
  appuntamenti: readonly AppuntamentoFinestra[],
  regolaMezzanotte: RegolaMezzanotte
): FinestraOraria {
  const intervallo = getIntervalloAppuntamenti(
    giorni,
    appuntamenti,
    regolaMezzanotte
  );
  if (intervallo === null) return { ...finestra };
  return arrotonda({
    inizio: Math.min(finestra.startHour * 60, intervallo.inizio),
    fine: Math.max(finestra.endHour * 60, intervallo.fine),
  });
}

/**
 * Finestra della funzione «Solo orario» (Ore «Prenotabili» nel riquadro
 * Visualizzazione): dall'apertura alla chiusura (la banda calendar dello
 * sfondo, la parte bianca della griglia; i turni non contano), allargata agli
 * appuntamenti. Se nessuno dei giorni ha orari (chiuso, festivo,
 * negozio senza orari, sfondo in errore) resta quella di sempre del calendario,
 * allargata solo da un appuntamento fuori da quelle ore.
 *
 * Funzione uguale nei due frontend. Chi la chiama passa:
 * - `giorni`: i giorni mostrati, 'YYYY-MM-DD', non quelli caricati (in 3 Giorni
 *   se ne caricano 4) e, nel Lavorativo, senza i giorni collassati;
 * - `orari`: la banda calendar dello sfondo, 'YYYY-MM-DD' → fasce, oppure
 *   l'elenco delle fasce di tutte le bande del giorno, che si uniscono;
 * - `appuntamenti`: la lista dell'agenda, senza le festività;
 * - `predefinita`: le ore di sempre del calendario, 5-24 se non indicate.
 */
export function calcolaFinestraOraria(
  giorni: readonly string[],
  orari: OrariFinestra | undefined,
  appuntamenti: readonly AppuntamentoFinestra[],
  regolaMezzanotte: RegolaMezzanotte,
  predefinita: Readonly<FinestraOraria> = FINESTRA_PREDEFINITA
): FinestraOraria {
  const fasce = getIntervalloFasce(giorni, orari);
  return allargaFinestraOraria(
    fasce === null ? predefinita : arrotonda(fasce),
    giorni,
    appuntamenti,
    regolaMezzanotte
  );
}
